import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
    Plus, Search, ClipboardEdit, Loader2, Trash2, Edit3, Copy, Code,
    ExternalLink, Eye, ToggleLeft, ToggleRight, FileText, Users, BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Forms() {
    const { getAuthHeader, user } = useAuth();
    const navigate = useNavigate();
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [createName, setCreateName] = useState('');
    const [creating, setCreating] = useState(false);
    const [embedForm, setEmbedForm] = useState(null);
    const [subsForm, setSubsForm] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [subsLoading, setSubsLoading] = useState(false);

    const loadForms = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/forms`, { headers: getAuthHeader() });
            setForms(res.data || []);
        } catch { toast.error('Failed to load forms'); }
        finally { setLoading(false); }
    }, [getAuthHeader]);

    useEffect(() => { loadForms(); }, [loadForms]);

    const handleCreate = async () => {
        if (!createName.trim()) { toast.error('Form name required'); return; }
        setCreating(true);
        try {
            const res = await axios.post(`${API}/forms`, { name: createName }, { headers: getAuthHeader() });
            toast.success('Form created');
            setShowCreate(false);
            setCreateName('');
            navigate(`/forms/${res.data.form_id}/edit`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
        finally { setCreating(false); }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/forms/${id}`, { headers: getAuthHeader() });
            toast.success('Form deleted');
            loadForms();
        } catch { toast.error('Failed to delete'); }
    };

    const handleToggle = async (form) => {
        try {
            await axios.put(`${API}/forms/${form.form_id}`, { is_active: !form.is_active }, { headers: getAuthHeader() });
            toast.success(form.is_active ? 'Form deactivated' : 'Form activated');
            loadForms();
        } catch { toast.error('Failed to update'); }
    };

    const copyUrl = (slug) => {
        navigator.clipboard.writeText(`https://app.nurturecrm.ai/form/${slug}`);
        toast.success('Public URL copied');
    };

    const loadSubmissions = async (form) => {
        setSubsForm(form);
        setSubsLoading(true);
        try {
            const res = await axios.get(`${API}/forms/${form.form_id}/submissions?page_size=50`, { headers: getAuthHeader() });
            setSubmissions(res.data?.data || []);
        } catch { toast.error('Failed to load submissions'); }
        finally { setSubsLoading(false); }
    };

    const exportCsv = async (formId) => {
        try {
            const res = await axios.get(`${API}/forms/${formId}/submissions/export`, {
                headers: getAuthHeader(), responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a'); a.href = url;
            a.download = `form-submissions-${formId}.csv`; a.click();
            window.URL.revokeObjectURL(url);
        } catch { toast.error('Export failed'); }
    };

    const filtered = forms.filter(f => f.name?.toLowerCase().includes(search.toLowerCase()));

    if (loading) {
        return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 text-stone-400 animate-spin" /></div>;
    }

    return (
        <div className="min-h-screen bg-stone-50 p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Forms</h1>
                    <p className="text-sm text-stone-500 mt-1">Create embeddable forms to capture leads and trigger workflows</p>
                </div>
                {(user?.is_admin || user?.is_super_admin) && (
                    <Button onClick={() => setShowCreate(true)}>
                        <Plus className="w-4 h-4 mr-1.5" /> New Form
                    </Button>
                )}
            </div>

            {/* KPI */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <ClipboardEdit className="w-4 h-4 text-stone-400" />
                        <span className="text-xs text-stone-500">Total Forms</span>
                    </div>
                    <span className="text-2xl font-bold text-stone-900">{forms.length}</span>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <ToggleRight className="w-4 h-4 text-stone-400" />
                        <span className="text-xs text-stone-500">Active</span>
                    </div>
                    <span className="text-2xl font-bold text-stone-900">{forms.filter(f => f.is_active).length}</span>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4 text-stone-400" />
                        <span className="text-xs text-stone-500">Total Submissions</span>
                    </div>
                    <span className="text-2xl font-bold text-stone-900">{forms.reduce((s, f) => s + (f.submission_count || 0), 0)}</span>
                </div>
            </div>

            {/* Search */}
            <div className="relative max-w-sm mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search forms..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
            </div>

            {/* Form grid */}
            {filtered.length === 0 ? (
                <div className="text-center py-16">
                    <ClipboardEdit className="w-12 h-12 text-stone-200 mx-auto mb-3" />
                    <p className="text-stone-400">No forms yet. Create your first one!</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(f => (
                        <div key={f.form_id} className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-semibold text-stone-900 truncate">{f.name}</h3>
                                    <span className="text-xs text-stone-400 font-mono">/{f.slug}</span>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${f.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-500'}`}>
                                    {f.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                            {f.description && <p className="text-xs text-stone-500 mb-2 line-clamp-2">{f.description}</p>}
                            <div className="flex items-center gap-4 text-xs text-stone-500 mb-3">
                                <span>{f.fields?.length || 0} fields</span>
                                <span>{f.submission_count || 0} submissions</span>
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <Button variant="outline" size="sm" onClick={() => navigate(`/forms/${f.form_id}/edit`)}>
                                    <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => copyUrl(f.slug)}>
                                    <Copy className="w-3.5 h-3.5 mr-1" /> URL
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => setEmbedForm(f)}>
                                    <Code className="w-3.5 h-3.5 mr-1" /> Embed
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => loadSubmissions(f)}>
                                    <Eye className="w-3.5 h-3.5 mr-1" /> {f.submission_count || 0}
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => handleToggle(f)}>
                                    {f.is_active ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                                </Button>
                                <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDelete(f.form_id, f.name)}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Modal */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader><DialogTitle>Create Form</DialogTitle></DialogHeader>
                    <div className="py-2">
                        <label className="block text-xs font-medium text-stone-600 mb-1">Form Name</label>
                        <input value={createName} onChange={e => setCreateName(e.target.value)}
                            placeholder="e.g. Webinar Registration" autoFocus
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            onKeyDown={e => e.key === 'Enter' && handleCreate()} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                        <Button onClick={handleCreate} disabled={creating}>{creating ? 'Creating...' : 'Create Form'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Embed Code Modal */}
            <Dialog open={!!embedForm} onOpenChange={() => setEmbedForm(null)}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader><DialogTitle>Embed Code — {embedForm?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Public URL</label>
                            <div className="flex items-center gap-2">
                                <code className="flex-1 text-xs bg-stone-50 px-3 py-2 rounded border border-stone-200 truncate">
                                    https://app.nurturecrm.ai/form/{embedForm?.slug}
                                </code>
                                <Button variant="outline" size="sm" onClick={() => copyUrl(embedForm?.slug)}><Copy className="w-3.5 h-3.5" /></Button>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">iframe Embed</label>
                            <textarea readOnly rows={3} className="w-full text-xs font-mono bg-stone-50 px-3 py-2 rounded border border-stone-200"
                                value={`<iframe src="https://app.nurturecrm.ai/form/${embedForm?.slug}?embed=true" width="100%" height="600" frameborder="0" style="border:none;max-width:640px;"></iframe>`} />
                            <Button variant="outline" size="sm" className="mt-1" onClick={() => {
                                navigator.clipboard.writeText(`<iframe src="https://app.nurturecrm.ai/form/${embedForm?.slug}?embed=true" width="100%" height="600" frameborder="0" style="border:none;max-width:640px;"></iframe>`);
                                toast.success('iframe code copied');
                            }}><Copy className="w-3.5 h-3.5 mr-1" /> Copy iframe</Button>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Script Tag (auto-resize)</label>
                            <textarea readOnly rows={4} className="w-full text-xs font-mono bg-stone-50 px-3 py-2 rounded border border-stone-200"
                                value={`<div id="nurture-form-${embedForm?.slug}"></div>\n<script>\n(function(){var d=document.getElementById('nurture-form-${embedForm?.slug}');if(!d)return;var f=document.createElement('iframe');f.src='https://app.nurturecrm.ai/form/${embedForm?.slug}?embed=true';f.style.cssText='width:100%;border:none;max-width:640px;min-height:400px;';f.setAttribute('frameborder','0');d.appendChild(f);window.addEventListener('message',function(e){if(e.data&&e.data.type==='nurture-form-resize'&&e.data.slug==='${embedForm?.slug}'){f.style.height=e.data.height+'px';}});})();\n</script>`} />
                            <Button variant="outline" size="sm" className="mt-1" onClick={() => {
                                navigator.clipboard.writeText(`<div id="nurture-form-${embedForm?.slug}"></div>\n<script>\n(function(){var d=document.getElementById('nurture-form-${embedForm?.slug}');if(!d)return;var f=document.createElement('iframe');f.src='https://app.nurturecrm.ai/form/${embedForm?.slug}?embed=true';f.style.cssText='width:100%;border:none;max-width:640px;min-height:400px;';f.setAttribute('frameborder','0');d.appendChild(f);window.addEventListener('message',function(e){if(e.data&&e.data.type==='nurture-form-resize'&&e.data.slug==='${embedForm?.slug}'){f.style.height=e.data.height+'px';}});})();\n</script>`);
                                toast.success('Script code copied');
                            }}><Copy className="w-3.5 h-3.5 mr-1" /> Copy script</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Submissions Modal */}
            <Dialog open={!!subsForm} onOpenChange={() => setSubsForm(null)}>
                <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center justify-between">
                            <span>Submissions — {subsForm?.name}</span>
                            <Button variant="outline" size="sm" onClick={() => exportCsv(subsForm?.form_id)}>
                                <FileText className="w-3.5 h-3.5 mr-1" /> Export CSV
                            </Button>
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto">
                        {subsLoading ? (
                            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
                        ) : submissions.length === 0 ? (
                            <p className="text-sm text-stone-400 py-8 text-center">No submissions yet</p>
                        ) : (
                            <div className="space-y-2">
                                {submissions.map(s => {
                                    const fieldsMap = {};
                                    (subsForm?.fields || []).forEach(f => { fieldsMap[f.field_id] = f.label; });
                                    return (
                                        <div key={s.submission_id} className="bg-stone-50 rounded-lg p-3 text-xs">
                                            <div className="flex items-center justify-between mb-1.5">
                                                <span className="text-stone-400">{new Date(s.created_at).toLocaleString()}</span>
                                                {s.contact_id && <span className="text-blue-500 font-medium">Contact linked</span>}
                                            </div>
                                            <div className="space-y-0.5">
                                                {Object.entries(s.answers || {}).map(([fid, val]) => (
                                                    <div key={fid}>
                                                        <span className="font-medium text-stone-600">{fieldsMap[fid] || fid}:</span>{' '}
                                                        <span className="text-stone-700">{String(val)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
